import { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
    onSubmit: (code: string) => void;
    onClose: () => void;
    loading: boolean;
    disabled: boolean;
    error: string | null;
}

const CODE_LENGTH = 6;
const GROUP_SIZE = 3;

export function CodeModal({ onSubmit, onClose, loading, disabled, error }: Props) {
    const [chars, setChars] = useState<string[]>(Array(CODE_LENGTH).fill(''));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Focus first input on mount
    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);

    const isFull = chars.every(c => c !== '');

    const focusIndex = (i: number) => {
        inputRefs.current[i]?.focus();
    };

    const handleChange = (index: number, value: string) => {
        // Take only the last character typed (handles overwrite)
        const char = value.slice(-1).toUpperCase();
        if (char && !/^[A-Z0-9]$/.test(char)) return;

        const next = [...chars];
        next[index] = char;
        setChars(next);

        // Auto-advance
        if (char && index < CODE_LENGTH - 1) {
            focusIndex(index + 1);
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace') {
            if (chars[index]) {
                const next = [...chars];
                next[index] = '';
                setChars(next);
            } else if (index > 0) {
                const next = [...chars];
                next[index - 1] = '';
                setChars(next);
                focusIndex(index - 1);
            }
            e.preventDefault();
        } else if (e.key === 'ArrowLeft' && index > 0) {
            focusIndex(index - 1);
        } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
            focusIndex(index + 1);
        } else if (e.key === 'Enter' && isFull && !loading && !disabled) {
            onSubmit(chars.join(''));
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        if (!pasted) return;

        const next = [...chars];
        for (let i = 0; i < Math.min(pasted.length, CODE_LENGTH); i++) {
            next[i] = pasted[i];
        }
        setChars(next);
        focusIndex(Math.min(pasted.length, CODE_LENGTH - 1));
    }, [chars]);

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="code-modal-backdrop" onClick={handleBackdropClick}>
            <div className="code-modal">
                <button className="code-modal-close" onClick={onClose}>&times;</button>
                <div className="code-modal-title">Enter Code</div>
                <div className="code-modal-inputs" onPaste={handlePaste}>
                    {Array.from({ length: CODE_LENGTH }, (_, i) => (
                        <span key={i} className="code-modal-cell-wrap">
                            <input
                                ref={el => { inputRefs.current[i] = el; }}
                                className={`code-modal-cell ${chars[i] ? 'filled' : ''}`}
                                type="text"
                                inputMode="text"
                                autoCapitalize="characters"
                                maxLength={2}
                                value={chars[i]}
                                onChange={e => handleChange(i, e.target.value)}
                                onKeyDown={e => handleKeyDown(i, e)}
                                disabled={loading}
                            />
                            {i === GROUP_SIZE - 1 && <span className="code-modal-dash">&ndash;</span>}
                        </span>
                    ))}
                </div>
                {error && <div className="code-modal-error" role="alert">{error}</div>}
                <button
                    className="code-modal-submit"
                    disabled={!isFull || loading || disabled}
                    onClick={() => onSubmit(chars.join(''))}
                >
                    {loading ? 'Looking...' : 'Go'}
                </button>
            </div>
        </div>
    );
}
